import { currencyFormatter } from "@common/helpers/currency";
import { pluralizeWithCount } from "@common/helpers/string";
import { ScheduleApplicationStatus } from "@common/types/domains/accounting/schedule";
import { formatDateOnly } from "@helpers/date";

export interface ScheduleCardValues {
    name: string;
    termEnd: string | null;
    remainingAmount: number;
    status?: ScheduleApplicationStatus;
    viaLineItemCount?: number;
    /** @default "left" */
    remainingSuffix?: "left" | "remaining";
}

/**
 * A schedule that is no longer recognizing says so in place of its end date;
 * an active one reads as the plain applied state with the end date.
 */
const STATUS_LABELS: Partial<Record<ScheduleApplicationStatus, string>> = {
    [ScheduleApplicationStatus.paused]: "Paused",
    [ScheduleApplicationStatus.stopped]: "Stopped",
    [ScheduleApplicationStatus.completed]: "Completed",
};

export interface ScheduleCardCopy {
    name: string;
    viaLineItemCount: number | null;
    lineItemCountLabel: string | null;
    viaLineItemsLabel: string | null;
    /** "Paused" / "Stopped" / "Completed"; null while the schedule is recognizing. */
    statusLabel: string | null;
    /** "Ends Dec 31"; null when a status label is shown or there are no periods yet. */
    endsLabel: string | null;
    /** "$4,200.00"; the card shows this alone. */
    amountLabel: string;
    /** "$4,200.00 remaining"; the tooltip's full line keeps the suffix. */
    remainingLabel: string;
}

export const SEGMENT_SEPARATOR_GLYPH = "·";
export const SEGMENT_SEPARATOR = ` ${SEGMENT_SEPARATOR_GLYPH} `;

export function buildScheduleCardCopy(
    values: ScheduleCardValues,
): ScheduleCardCopy {
    const statusLabel =
        values.status === undefined
            ? null
            : (STATUS_LABELS[values.status] ?? null);
    const viaLineItemCount =
        values.viaLineItemCount !== undefined && values.viaLineItemCount >= 1
            ? values.viaLineItemCount
            : null;
    const lineItemCountLabel =
        viaLineItemCount !== null
            ? pluralizeWithCount("line item", viaLineItemCount)
            : null;
    const amountLabel = currencyFormatter.format(values.remainingAmount);
    return {
        name: values.name,
        viaLineItemCount,
        lineItemCountLabel,
        viaLineItemsLabel:
            lineItemCountLabel !== null ? `Via ${lineItemCountLabel}` : null,
        statusLabel,
        endsLabel:
            statusLabel !== null || values.termEnd === null
                ? null
                : `Ends ${formatDateOnly(values.termEnd)}`,
        amountLabel,
        remainingLabel: `${amountLabel} ${values.remainingSuffix ?? "remaining"}`,
    };
}

/** The full one-line reading, for the tooltip when the name is cut off. */
export function formatScheduleCardText(copy: ScheduleCardCopy): string {
    return [
        copy.name,
        copy.viaLineItemsLabel,
        copy.statusLabel,
        copy.endsLabel,
        copy.remainingLabel,
    ]
        .filter(
            (segment): segment is string =>
                segment !== null && segment.trim() !== "",
        )
        .join(SEGMENT_SEPARATOR);
}
